import { defineStore } from 'pinia';
import { addDoc, collection, doc, getDoc, getDocs, query, updateDoc, deleteDoc, where } from 'firebase/firestore';
import type { FormAccessToken, FormField, FormPage, FormSubmission, FormSubmissionAnswer, PortalFormRequest, Tenant, TenantForm } from '~/types/models';
import { generateSlug, makeSlugUnique } from '~/lib/slugify';
import { stripUndefined } from '~/lib/stripUndefined';

function makeId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function cloneField(field: FormField, index = 0): FormField {
    return {
        ...field,
        id: field.id || makeId(`field-${index + 1}`),
        label: String(field.label || ''),
        type: field.type || 'text',
        required: field.required === true,
        width: field.width || 'full',
        options: (field.options || []).map((option, optionIndex) => ({
            id: option.id || makeId(`option-${optionIndex + 1}`),
            label: String(option.label || ''),
            value: String(option.value || ''),
        })),
    };
}

function clonePages(pages: FormPage[] = []): FormPage[] {
    return pages.map((page, index) => ({
        id: page.id || makeId(`page-${index + 1}`),
        title: page.title || '',
        description: page.description || '',
        fields: (page.fields || []).map((field, fieldIndex) => cloneField(field, fieldIndex)),
    }));
}

export function cloneFormDefinition(form: Partial<TenantForm>): Omit<TenantForm, 'id'> {
    return {
        tenantId: form.tenantId || '',
        name: String(form.name || ''),
        slug: String(form.slug || ''),
        description: form.description || '',
        kind: form.kind || 'lead_capture',
        mode: form.mode || 'both',
        status: form.status || 'draft',
        allowPublicAccess: form.allowPublicAccess === true,
        showFormType: form.showFormType !== false,
        showFormTitle: form.showFormTitle !== false,
        successMessage: form.successMessage || '',
        // Undefined deliberately retains the legacy experience for saved forms.
        showStartScreen: form.showStartScreen !== false,
        startButtonLabel: form.startButtonLabel || '',
        submitButtonLabel: form.submitButtonLabel || '',
        introTitle: form.introTitle || '',
        introText: form.introText || '',
        pages: clonePages(form.pages || []),
        themeOverrides: {
            ...(form.themeOverrides || {}),
        },
        defaultClientId: form.defaultClientId || '',
        defaultProjectId: form.defaultProjectId || '',
        createdBy: form.createdBy,
        updatedBy: form.updatedBy,
        createdAt: form.createdAt,
        updatedAt: form.updatedAt,
    };
}

function normalizeForm(form: TenantForm): TenantForm {
    return {
        ...form,
        pages: clonePages(form.pages || []),
        mode: form.mode || 'both',
        kind: form.kind || 'lead_capture',
        status: form.status || 'draft',
        allowPublicAccess: form.allowPublicAccess === true,
        showStartScreen: form.showStartScreen !== false,
    };
}

function normalizeSubmission(submission: FormSubmission): FormSubmission {
    return {
        ...submission,
        answers: Array.isArray(submission.answers) ? submission.answers : [],
        status: submission.status || 'new',
    };
}

type PublicFormDraftAnswer = Omit<FormSubmissionAnswer, 'value'> & {
    value: FormSubmissionAnswer['value'] | File[],
};

type FormSubmitInput = {
    tenantId: string;
    form: TenantForm;
    answers: PublicFormDraftAnswer[];
    portalFormRequestId?: string;
    sourceUrl?: string;
    linkedClientId?: string;
    linkedProjectId?: string;
    onboardingRunId?: string;
    onboardingRequirementId?: string;
    onboardingTaskId?: string;
    onboardingActionToken?: string;
};

type PublicInviteFormResponse = {
    form: TenantForm;
    tenant: Tenant | null;
    access: {
        id: string;
        status: NonNullable<FormAccessToken['status']>;
        draftAnswers: FormSubmissionAnswer[];
        recipientName?: string;
        recipientEmail?: string;
        projectName?: string;
        clientName?: string;
    };
};

type PublicPublishedFormResponse = {
    form: TenantForm;
    tenant: Tenant | null;
};

type PortalFormResponse = {
    form: TenantForm | null;
    request: PortalFormRequest | null;
    submission: FormSubmission | null;
};

type FormSubmitResult = {
    submissionId: string;
    clientId: string;
};

function buildFormSubmissionPayload(input: FormSubmitInput) {
    return {
        tenantId: input.tenantId,
        formId: input.form.id,
        portalFormRequestId: input.portalFormRequestId || undefined,
        answers: input.answers.map((answer) => ({
            fieldId: answer.fieldId,
            fieldLabel: answer.fieldLabel,
            value: Array.isArray(answer.value) && answer.value.some((item) => item instanceof File)
                ? null
                : answer.value,
        })),
        sourceUrl: input.sourceUrl || undefined,
        linkedClientId: input.linkedClientId || input.form.defaultClientId || undefined,
        linkedProjectId: input.linkedProjectId || input.form.defaultProjectId || undefined,
        onboardingRunId: input.onboardingRunId || undefined,
        onboardingRequirementId: input.onboardingRequirementId || undefined,
        onboardingTaskId: input.onboardingTaskId || undefined,
        onboardingActionToken: input.onboardingActionToken || undefined,
    };
}

async function submitFormRequest(endpoint: string, input: FormSubmitInput, query?: Record<string, string | undefined>) {
    const payload = buildFormSubmissionPayload(input);
    const uploadAnswers = input.answers.filter((answer) => (
        Array.isArray(answer.value) && answer.value.some((item) => item instanceof File)
    ));

    if (uploadAnswers.length === 0) {
        return await $fetch<FormSubmitResult>(endpoint, {
            method: 'POST',
            body: payload,
            query,
        });
    }

    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));

    for (const answer of uploadAnswers) {
        for (const file of answer.value.filter((item): item is File => item instanceof File)) {
            formData.append(`files:${answer.fieldId}`, file, file.name);
        }
    }

    return await $fetch<FormSubmitResult>(endpoint, {
        method: 'POST',
        body: formData,
        query,
    });
}

export function createDefaultForm(kind: TenantForm['kind'] = 'lead_capture'): Omit<TenantForm, 'id'> {
    if (kind === 'questionnaire') {
        return {
            tenantId: '',
            name: '',
            slug: '',
            description: '',
            kind,
            mode: 'both',
            status: 'draft',
            allowPublicAccess: true,
            showFormType: true,
            showFormTitle: true,
            successMessage: 'Thanks for completing the questionnaire. We have your answers.',
            showStartScreen: true,
            startButtonLabel: 'Start questionnaire',
            submitButtonLabel: 'Submit questionnaire',
            introTitle: 'Project questionnaire',
            introText: 'Use this form to gather the detail you need before or during a project.',
            pages: [
                {
                    id: makeId('page'),
                    title: 'Project Background',
                    description: 'Capture the context before work starts.',
                    fields: [
                        { id: makeId('field'), type: 'text', label: 'Your name', required: true, width: 'half' },
                        { id: makeId('field'), type: 'email', label: 'Your email', required: true, width: 'half' },
                        { id: makeId('field'), type: 'textarea', label: 'Tell us about your goals', required: true, width: 'full' },
                    ],
                },
            ],
            themeOverrides: {},
            createdAt: undefined,
            updatedAt: undefined,
        };
    }

    return {
        tenantId: '',
        name: '',
        slug: '',
        description: '',
        kind,
        mode: 'both',
        status: 'draft',
        allowPublicAccess: true,
        showFormType: true,
        showFormTitle: true,
        successMessage: 'Thanks, we have received your message and will be in touch shortly.',
        showStartScreen: false,
        startButtonLabel: 'Start form',
        submitButtonLabel: 'Send enquiry',
        introTitle: 'Start your project',
        introText: 'Tell us a little about what you need and we will follow up with the right next step.',
        pages: [
            {
                id: makeId('page'),
                title: 'Contact details',
                description: 'Start with the basics.',
                fields: [
                    { id: makeId('field'), type: 'text', label: 'Your name', required: true, width: 'half' },
                    { id: makeId('field'), type: 'email', label: 'Email address', required: true, width: 'half' },
                    { id: makeId('field'), type: 'phone', label: 'Phone number', required: false, width: 'half' },
                    { id: makeId('field'), type: 'text', label: 'Company', required: false, width: 'half' },
                    { id: makeId('field'), type: 'textarea', label: 'What do you need help with?', required: true, width: 'full' },
                ],
            },
        ],
        themeOverrides: {},
        createdAt: undefined,
        updatedAt: undefined,
    };
}

export function createBlankForm(): Omit<TenantForm, 'id'> {
    const base = createDefaultForm('lead_capture');
    return {
        ...base,
        introTitle: '',
        introText: '',
        successMessage: 'Thanks, your response has been received.',
        pages: [{
            id: makeId('page'),
            title: 'Page 1',
            description: '',
            fields: [{
                id: makeId('field'),
                type: 'text',
                label: '',
                required: false,
                width: 'full',
            }],
        }],
    };
}

export const useFormStore = defineStore('forms', {
    state: () => ({
        forms: [] as TenantForm[],
        submissions: [] as FormSubmission[],
        portalRequests: [] as PortalFormRequest[],
        currentForm: null as TenantForm | null,
        loading: false,
        portalRequestsLoading: false,
        portalRequestsError: null as string | null,
        error: null as string | null,
    }),

    actions: {
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        _formsCollection(tenantId = this._getTenantId()) {
            const { $db } = useNuxtApp();
            return collection($db, `tenants/${tenantId}/forms`);
        },

        _submissionsCollection(tenantId = this._getTenantId()) {
            const { $db } = useNuxtApp();
            return collection($db, `tenants/${tenantId}/formSubmissions`);
        },

        async fetchForms() {
            this.loading = true;
            this.error = null;

            try {
                const snapshot = await getDocs(this._formsCollection());
                this.forms = snapshot.docs
                    .map((entry) => normalizeForm({ id: entry.id, ...entry.data() } as TenantForm))
                    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
            } catch (error: any) {
                this.error = error?.message || 'Failed to load forms.';
                console.error('Error fetching forms:', error);
            } finally {
                this.loading = false;
            }
        },

        async fetchSubmissions() {
            this.loading = true;
            this.error = null;

            try {
                const snapshot = await getDocs(this._submissionsCollection());
                this.submissions = snapshot.docs
                    .map((entry) => normalizeSubmission({ id: entry.id, ...entry.data() } as FormSubmission))
                    .sort((a, b) => new Date(String(b.createdAt || 0)).getTime() - new Date(String(a.createdAt || 0)).getTime());
            } catch (error: any) {
                this.error = error?.message || 'Failed to load submissions.';
                console.error('Error fetching form submissions:', error);
            } finally {
                this.loading = false;
            }
        },

        async fetchPortalRequests(options?: { silent?: boolean }) {
            const tenantId = this._getTenantId();
            const { $auth } = useNuxtApp();
            const authUser = $auth.currentUser;
            if (!authUser) {
                this.portalRequests = [];
                return;
            }

            if (!options?.silent) this.portalRequestsLoading = true;
            this.portalRequestsError = null;

            try {
                const response = await $fetch<{ requests: PortalFormRequest[] }>('/api/forms/portal-requests', {
                    query: { tenantId },
                    headers: {
                        authorization: `Bearer ${await authUser.getIdToken()}`,
                    },
                });
                this.portalRequests = Array.isArray(response.requests) ? response.requests : [];
            } catch (error: any) {
                this.portalRequestsError = error?.data?.statusMessage || error?.message || 'Failed to load portal form progress.';
                console.error('Error fetching portal form progress:', error);
            } finally {
                this.portalRequestsLoading = false;
            }
        },

        async fetchAll() {
            await Promise.all([this.fetchForms(), this.fetchSubmissions(), this.fetchPortalRequests()]);
        },

        async fetchFormById(formId: string): Promise<TenantForm | null> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();

            try {
                const snapshot = await getDoc(doc($db, `tenants/${tenantId}/forms`, formId));
                if (!snapshot.exists()) {
                    this.currentForm = null;
                    return null;
                }

                const form = normalizeForm({ id: snapshot.id, ...snapshot.data() } as TenantForm);
                this.currentForm = form;

                const index = this.forms.findIndex((entry) => entry.id === form.id);
                if (index === -1) {
                    this.forms.push(form);
                    this.forms.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
                } else {
                    this.forms[index] = form;
                }

                return form;
            } catch (error: any) {
                this.error = error?.message || 'Failed to load form.';
                console.error('Error fetching form by id:', error);
                this.currentForm = null;
                return null;
            }
        },

        async addForm(payload: Omit<TenantForm, 'id' | 'tenantId' | 'slug' | 'createdAt' | 'updatedAt'>) {
            const tenantId = this._getTenantId();
            const slug = makeSlugUnique(generateSlug(payload.name || 'form'));
            const document = stripUndefined({
                ...payload,
                tenantId,
                slug,
                pages: clonePages(payload.pages || []),
                createdAt: new Date(),
                updatedAt: new Date(),
            });

            const docRef = await addDoc(this._formsCollection(), document);
            this.forms.push(normalizeForm({ id: docRef.id, ...document } as TenantForm));
            this.forms.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
            return docRef.id;
        },

        async updateForm(formId: string, payload: Partial<TenantForm>) {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const normalized = stripUndefined({
                ...payload,
                ...(payload.pages ? { pages: clonePages(payload.pages) } : {}),
                updatedAt: new Date(),
            });

            await updateDoc(doc($db, `tenants/${tenantId}/forms`, formId), normalized);
            const index = this.forms.findIndex((form) => form.id === formId);
            if (index !== -1) {
                this.forms[index] = normalizeForm({ ...this.forms[index], ...normalized } as TenantForm);
            }
        },

        async deleteForm(formId: string) {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            await deleteDoc(doc($db, `tenants/${tenantId}/forms`, formId));
            this.forms = this.forms.filter((form) => form.id !== formId);
        },

        async fetchPublicFormBySlug(tenantId: string, slug: string): Promise<PublicPublishedFormResponse | null> {
            try {
                const response = await $fetch<PublicPublishedFormResponse>('/api/public/forms/by-slug', {
                    query: {
                        tenantId,
                        slug,
                    },
                });
                this.currentForm = normalizeForm(response.form);
                return {
                    ...response,
                    form: this.currentForm,
                };
            } catch (error) {
                this.currentForm = null;
                throw error;
            }
        },

        async fetchPortalFormBySlug(input: {
            tenantId: string;
            slug: string;
            clientId: string;
            projectId?: string;
            preview?: boolean;
            previewClientId?: string;
        }): Promise<PortalFormResponse> {
            try {
                const response = await $fetch<PortalFormResponse>(`/api/client-portal/forms/${encodeURIComponent(input.slug)}`, {
                    query: {
                        tenantId: input.tenantId,
                        clientId: input.clientId,
                        projectId: input.projectId,
                        preview: input.preview ? '1' : undefined,
                        asClientId: input.previewClientId,
                    },
                });
                this.currentForm = response.form ? normalizeForm(response.form) : null;
                return {
                    ...response,
                    form: this.currentForm,
                };
            } catch (error) {
                this.currentForm = null;
                throw error;
            }
        },

        async fetchPublicInviteForm(token: string) {
            const response = await $fetch<PublicInviteFormResponse>(`/api/public/forms/${encodeURIComponent(token)}`);
            this.currentForm = normalizeForm(response.form);
            return {
                ...response,
                form: this.currentForm,
            };
        },

        async submitPublicForm(input: FormSubmitInput) {
            return await submitFormRequest('/api/forms/public-submit', input);
        },

        async savePublicInviteDraft(token: string, answers: FormSubmissionAnswer[]) {
            return await $fetch<{ ok: true; savedAt: string }>(`/api/public/forms/${encodeURIComponent(token)}/draft`, {
                method: 'POST',
                body: {
                    answers,
                },
            });
        },

        async submitPublicInviteForm(input: FormSubmitInput & { token: string }) {
            return await submitFormRequest(`/api/public/forms/${encodeURIComponent(input.token)}/submit`, input);
        },

        async submitPortalForm(input: FormSubmitInput & {
            clientId: string;
            preview?: boolean;
            previewClientId?: string;
        }) {
            return await submitFormRequest('/api/client-portal/forms/submit', input, {
                tenantId: input.tenantId,
                clientId: input.clientId,
                projectId: input.linkedProjectId,
                preview: input.preview ? '1' : undefined,
                asClientId: input.previewClientId,
            });
        },

        async linkSubmission(submissionId: string, payload: Pick<FormSubmission, 'linkedClientId' | 'linkedProjectId' | 'status'>) {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const normalized = stripUndefined({
                ...payload,
                updatedAt: new Date(),
            });

            await updateDoc(doc($db, `tenants/${tenantId}/formSubmissions`, submissionId), normalized);
            const index = this.submissions.findIndex((submission) => submission.id === submissionId);
            if (index !== -1) {
                this.submissions[index] = normalizeSubmission({ ...this.submissions[index], ...normalized } as FormSubmission);
            }
        },
    },
});
